import React, { useState } from 'react';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp?: string;
}

interface ChatSelectionSectionProps {
  chatHistory: ChatMessage[];
  selected: boolean;
  onToggle: () => void;
  /**
   * How many trailing messages the backend actually feeds into the
   * regeneration prompt. Anything beyond this is dropped server-side.
   */
  messageLimit?: number;
  className?: string;
}

/**
 * Component for selecting chat history in the regenerate modal
 * Shows chat summary with expandable preview
 */
export const ChatSelectionSection: React.FC<ChatSelectionSectionProps> = ({
  chatHistory,
  selected,
  onToggle,
  messageLimit,
  className = ''
}) => {
  const [isPreviewExpanded, setIsPreviewExpanded] = useState(false);

  if (chatHistory.length === 0) {
    return null;
  }

  const messageCount = chatHistory.length;
  const userMessages = chatHistory.filter(msg => msg.role === 'user').length;
  const assistantMessages = chatHistory.filter(msg => msg.role === 'assistant').length;
  const isTruncated = !!messageLimit && messageCount > messageLimit;

  // Get recent messages for preview (last 3 exchanges)
  const recentMessages = chatHistory.slice(-6); // Last 6 messages (3 exchanges)

  const formatTimestamp = (timestamp?: string) => {
    if (!timestamp) return '';
    return new Date(timestamp).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const truncateMessage = (content: string, maxLength: number = 100) => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + '...';
  };

  return (
    <div className={`chat-selection-section ${className}`}>
      {/* Section Header */}
      <div className="mb-3">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
          Chat Conversation
        </h4>
        <p className="text-xs text-gray-600 dark:text-gray-400">
          Include previous Q&A to focus the regenerated report on discussed topics
        </p>
      </div>

      {/* Chat Selection Card */}
      <div
        className={`
          border rounded-lg p-4 transition-colors cursor-pointer
          ${selected 
            ? 'border-purple-300 dark:border-purple-600 bg-purple-50 dark:bg-purple-900/20' 
            : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800'
          }
          hover:border-purple-300 dark:hover:border-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20
        `}
        onClick={onToggle}
      >
        {/* Selection Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-3">
            {/* Checkbox */}
            <input
              type="checkbox"
              checked={selected}
              onChange={onToggle}
              className="h-4 w-4 text-purple-600 rounded border-gray-300 focus:ring-purple-500"
              onClick={(e) => e.stopPropagation()}
            />

            {/* Chat Icon */}
            <span className="text-lg" aria-label="Chat conversation">
              💬
            </span>

            {/* Chat Info */}
            <div>
              <div className="flex items-center space-x-2">
                <span className="font-medium text-sm text-gray-900 dark:text-white">
                  Conversation History
                </span>
                {selected && (
                  <span className="inline-block text-xs px-2 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-200 border border-purple-200 dark:border-purple-700 font-medium">
                    Included
                  </span>
                )}
              </div>
              
              {/* Message Stats */}
              <div className="flex items-center space-x-3 text-xs text-gray-500 dark:text-gray-400 mt-1">
                <span>{messageCount} total messages</span>
                <span>•</span>
                <span>{userMessages} questions</span>
                <span>•</span>
                <span>{assistantMessages} responses</span>
              </div>

              {isTruncated && (
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                  Only the last {messageLimit} of {messageCount} messages will be included
                </p>
              )}
            </div>
          </div>

          {/* Selection Indicator */}
          {selected && (
            <div className="flex-shrink-0">
              <div className="w-5 h-5 bg-purple-600 rounded-full flex items-center justify-center">
                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
          )}
        </div>

        {/* Chat Preview Toggle */}
        <div className="mt-3">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsPreviewExpanded(!isPreviewExpanded);
            }}
            className="flex items-center space-x-1 text-xs text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
          >
            <span>
              {isPreviewExpanded ? 'Hide' : 'Show'} recent messages
            </span>
            <svg 
              className={`w-3 h-3 transition-transform ${ isPreviewExpanded ? 'rotate-180' : ''}`}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>

        {/* Expandable Chat Preview */}
        {isPreviewExpanded && (
          <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
            <div className="max-h-32 overflow-y-auto space-y-2">
              {recentMessages.map((message, index) => (
                <div key={index} className="text-xs">
                  <div className="flex items-start space-x-2">
                    <span className={`
                      font-medium flex-shrink-0
                      ${message.role === 'user' 
                        ? 'text-blue-600 dark:text-blue-400' 
                        : 'text-green-600 dark:text-green-400'
                      }
                    `}>
                      {message.role === 'user' ? '👤' : '🤖'}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-1">
                        <span className={`
                          font-medium
                          ${message.role === 'user' 
                            ? 'text-blue-700 dark:text-blue-300' 
                            : 'text-green-700 dark:text-green-300'
                          }
                        `}>
                          {message.role === 'user' ? 'You' : 'Assistant'}
                        </span>
                        {message.timestamp && (
                          <span className="text-gray-400 dark:text-gray-500">
                            {formatTimestamp(message.timestamp)}
                          </span>
                        )}
                      </div>
                      <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                        {truncateMessage(message.content)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {chatHistory.length > recentMessages.length && (
              <div className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                  +{chatHistory.length - recentMessages.length} earlier messages
                </p>
              </div>
            )}
          </div>
        )}

        {/* Information Footer */}
        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
          <div className="flex items-start space-x-2">
            <span className="text-xs text-blue-600 dark:text-blue-400 mt-0.5">ℹ️</span>
            <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
              {selected ? (
                <span>
                  The regenerated report will incorporate insights from your conversation, 
                  focusing on topics you've discussed and questions you've asked.
                </span>
              ) : (
                <span>
                  Enable to have the AI consider your previous questions and discussions 
                  when regenerating the report.
                </span>
              )}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatSelectionSection;